import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslations } from 'next-intl';
import { ArrowDown, ArrowUp } from '../../icons/arrow/arrow';
import CloseIcon from '../../icons/close/close';
import BurgerRight from '../../icons/burger-right/burgerRight';
import ShortArrowDown from '../../icons/shortArrowDown.svg';
import StyledAnchorMenu from './anchorMenu.style';
import Headline from '../../atoms/headline/headline';

const AnchorMenu = ({ links }) => {
  const t = useTranslations();
  const [showElement, setShowElement] = useState(false);
  const [isBackToTop, setIsBackToTop] = useState(false);
  const anchorMenu = useRef(null);

  const handleToggle = () => {
    setShowElement(!showElement);
  };

  const handleBackToTopScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const { scrollHeight } = document.documentElement;
    const { clientHeight } = document.documentElement;

    if (scrollHeight - scrollTop - clientHeight < 1600) {
      setIsBackToTop(true);
    } else {
      setIsBackToTop(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleBackToTopScroll);

    return () => {
      window.removeEventListener('scroll', handleBackToTopScroll);
    };
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleClick = e => {
    const targetId = e.target.closest('button').getAttribute('data-target');
    const targetElement = document.getElementById(targetId);

    window.history.replaceState(null, null, `#${targetId}`);
    targetElement.scrollIntoView({ behavior: 'smooth' });

    setTimeout(() => {
      targetElement.setAttribute('tabindex', '-1');
      targetElement.focus();
    }, 1000);

    handleToggle();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleClick(e);
    }
  };

  return (
    <StyledAnchorMenu ref={anchorMenu}>
      <div className={`anchorMenu__card ${showElement && 'anchorMenu__card--open'}`}>
        {showElement && !isBackToTop && (
          <div className="anchorMenu__nav">
            <Headline tag="span" className="anchorMenu__headline">
              {t('jumpTo')}
            </Headline>
            <div className="anchorMenu__items">
              {links.map(link => (
                <button
                  type="button"
                  key={link.label}
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                  data-target={link.href}
                  className="anchorMenu__item"
                >
                  <ShortArrowDown width="22" height="22" />
                  <Headline className="anchorLinks__link" subline tag="span">
                    {link.label}
                  </Headline>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {isBackToTop ? (
        <button
          type="button"
          onClick={handleBackToTop}
          className="anchorMenu__back-to-top"
          aria-label="To top"
        >
          <ArrowUp aria-hidden="true" />
          <Headline subline tag="span" className="anchorMenu__back-to-top-title">
            Beam me up scotty
          </Headline>
        </button>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          className={`anchorMenu__buttons ${showElement && 'anchorMenu__buttons--active'}`}
        >
          {showElement ? (
            <span className="anchorMenu__close-button">
              <CloseIcon />
            </span>
          ) : (
            <span className="anchorMenu__arrow-button">
              <ArrowDown />
            </span>
          )}
          <span className="anchorMenu__burger-button">
            <BurgerRight />
          </span>
        </button>
      )}
    </StyledAnchorMenu>
  );
};

AnchorMenu.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      label: PropTypes.string
    })
  )
};

export default AnchorMenu;
